import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from 'recharts';
import { notificar } from '../utils/notify';
import '../styles/memora_op.css';

const API = process.env.REACT_APP_API_BASE;

const ETAPAS = [
  { key: 'lead',         label: 'Lead',         cor: '#94a3b8', ordem: 0 },
  { key: 'qualificacao', label: 'Qualificação',  cor: '#60a5fa', ordem: 1 },
  { key: 'proposta',     label: 'Proposta',      cor: '#0d6efd', ordem: 2 },
  { key: 'negociacao',   label: 'Negociação',    cor: '#fd7e14', ordem: 3 },
  { key: 'finalizado',   label: 'Finalizado',    cor: '#198754', ordem: 4 },
  { key: 'perdido',      label: 'Perdido',       cor: '#dc3545', ordem: 5 },
];

const ALERT_INFO = {
  sem_followup:    { label: 'Sem follow-up',   icon: '', cls: 'danger' },
  parada:          { label: '+10 dias parada',  icon: '', cls: 'primary' },
  muitas_objecoes: { label: 'Muitas objeções',  icon: '', cls: 'primary' },
  baixo_interesse: { label: 'Baixo interesse',  icon: '', cls: 'secondary' },
};

function normStage(s) {
  if (!s) return 'lead';
  const t = (s + '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  if (t.includes('qualific')) return 'qualificacao';
  if (t.includes('propost'))  return 'proposta';
  if (t.includes('negoc'))    return 'negociacao';
  if (t.includes('finaliz') || t.includes('ganho') || t.includes('fecha') || t.includes('vend')) return 'finalizado';
  if (t.includes('perd') || t.includes('cancel') || t.includes('perdid')) return 'perdido';
  return 'lead';
}

// Interesse pode vir numérico (7) ou textual (alto/medio/baixo) — normaliza para 0-10
function interesseNum(v) {
  const n = parseInt(v);
  if (!isNaN(n)) return n;
  const t = (v || '').toString().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  if (t.includes('alto')) return 8;
  if (t.includes('medio')) return 5;
  if (t.includes('baixo')) return 2;
  return 0;
}

function badgeNota(nota) {
  const n = parseFloat(nota) || 0;
  if (n >= 8) return 'bg-success';
  if (n >= 6) return 'bg-primary';
  if (n >= 4) return 'bg-info text-dark';
  return 'bg-danger';
}

function textoProximo(p) {
  if (typeof p === 'string') return p;
  return p?.acao || p?.descricao || p?.texto || p?.prazo || JSON.stringify(p);
}

// Chance de fechamento estimada a partir dos sinais da própria oportunidade:
// interesse, etapa do funil, nº de reuniões, objeções, alertas e tempo parada.
function chanceFechamento(op) {
  const stage = normStage(op.estagio_venda);
  if (stage === 'finalizado') return { pct: 100, label: 'Fechada', cor: '#198754', fatores: ['Venda finalizada'] };
  if (stage === 'perdido')    return { pct: 0,   label: 'Perdida', cor: '#dc3545', fatores: ['Oportunidade perdida'] };

  const fatores = [];
  const interesse = interesseNum(op.interesse_nivel);
  let pct = interesse * 5;
  fatores.push(`Interesse do cliente ${interesse}/10`);

  const etapa = ETAPAS.find(e => e.key === stage) || ETAPAS[0];
  if (etapa.ordem > 0) {
    pct += etapa.ordem * 12;
    fatores.push(`Avançou no funil até ${etapa.label}`);
  }

  if ((op.total_reunioes || 1) > 1) {
    pct += 6;
    fatores.push(`${op.total_reunioes} reuniões realizadas (relacionamento ativo)`);
  }

  const nObj = (op.objecoes || []).length;
  if (nObj > 0) {
    pct -= Math.min(nObj * 3, 12);
    fatores.push(`${nObj} objeç${nObj > 1 ? 'ões' : 'ão'} levantada${nObj > 1 ? 's' : ''}`);
  }

  const nAlertas = (op.alertas || []).length;
  if (nAlertas > 0) {
    pct -= Math.min(nAlertas * 6, 18);
    fatores.push(`${nAlertas} alerta${nAlertas > 1 ? 's' : ''} ativo${nAlertas > 1 ? 's' : ''}`);
  }

  if (op.dias_desde_reuniao > 15) {
    pct -= 10;
    fatores.push(`Sem reunião há ${op.dias_desde_reuniao} dias`);
  } else if (op.dias_desde_reuniao > 7) {
    pct -= 5;
    fatores.push(`Última reunião há ${op.dias_desde_reuniao} dias`);
  }

  pct = Math.max(2, Math.min(97, Math.round(pct)));
  if (pct >= 70) return { pct, label: 'Alta',  cor: '#198754', fatores };
  if (pct >= 40) return { pct, label: 'Média', cor: '#0d6efd', fatores };
  return { pct, label: 'Baixa', cor: '#dc3545', fatores };
}

function textoObjecao(o) {
  if (typeof o === 'string') return o;
  return o?.descricao || o?.texto || o?.objecao || o?.objeção || JSON.stringify(o);
}

// Classifica a objeção em uma categoria para o gráfico ficar legível
const CATEGORIAS_OBJECAO = [
  { key: 'preco',       label: 'Preço / Custo',        cor: '#dc3545', kw: ['preco', 'preço', 'custo', 'orcamento', 'orçamento', 'caro', 'valor', 'pagar', 'investimento', 'desconto', 'negociacao de preco'] },
  { key: 'prazo',       label: 'Prazo / Timing',       cor: '#fd7e14', kw: ['prazo', 'tempo', 'timing', 'demora', 'implementacao', 'implementação', 'quando', 'agora nao', 'momento', 'urgencia', 'urgência'] },
  { key: 'escopo',      label: 'Escopo / Solução',     cor: '#0d6efd', kw: ['escopo', 'funcionalidade', 'modulo', 'módulo', 'servico', 'serviço', 'solucao', 'solução', 'nao atende', 'falta', 'recurso', 'integra'] },
  { key: 'concorrencia',label: 'Concorrência',         cor: '#6610f2', kw: ['concorrente', 'concorrencia', 'concorrência', 'outro fornecedor', 'fornecedor atual', 'ja temos', 'já temos', 'ja usamos', 'trocar de'] },
  { key: 'confianca',   label: 'Confiança / Risco',    cor: '#00205b', kw: ['confianca', 'confiança', 'risco', 'garantia', 'seguranca', 'segurança', 'medo', 'receio', 'duvida', 'dúvida', 'incerteza'] },
  { key: 'decisao',     label: 'Decisão Interna',      cor: '#20c997', kw: ['socio', 'sócio', 'diretoria', 'aprovacao', 'aprovação', 'decisao', 'decisão', 'consultar', 'conversar internamente', 'avaliar internamente', 'equipe'] },
];

function categorizarObjecao(texto) {
  const t = (texto || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  for (const cat of CATEGORIAS_OBJECAO) {
    if (cat.kw.some(k => t.includes(k))) return cat;
  }
  return { key: 'outras', label: 'Outras', cor: '#6c757d' };
}

// Tooltip customizado (branco) — o padrão do recharts é pintado de navy pelo fmpcustom.css
const TooltipObjecoes = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div style={{
      background: '#ffffff', border: '1px solid #e9ecef', borderRadius: 8,
      boxShadow: '0 4px 12px rgba(0,0,0,0.12)', padding: '10px 14px', fontSize: '0.87rem',
    }}>
      <div className="fw-bold mb-1" style={{ color: '#212529' }}>
        <span style={{
          display: 'inline-block', width: 10, height: 10, borderRadius: 3,
          background: d.payload?.cor || '#0d6efd', marginRight: 6,
        }}></span>
        {d.payload?.label}
      </div>
      <div style={{ color: '#6c757d' }}>
        {d.value} ocorrência{d.value !== 1 ? 's' : ''}
      </div>
    </div>
  );
};

export default function Oportunidades_memora() {
  const navigate = useNavigate();
  const [dados, setDados]                         = useState(null);
  const [carregando, setCarregando]               = useState(true);
  const [erro, setErro]                           = useState(null);
  const [filtroEtapa, setFiltroEtapa]             = useState('todas');
  const [filtroAlerta, setFiltroAlerta]           = useState('todos');
  const [busca, setBusca]                         = useState('');
  const [expandido, setExpandido]                 = useState(null);
  const [estagioOverrides, setEstagioOverrides]   = useState({});
  const [atualizando, setAtualizando]             = useState(null);
  const [modalReunioes, setModalReunioes]         = useState(null);
  const [estrategia, setEstrategia]               = useState('');
  const [carregandoEstrategia, setCarregandoEstrategia] = useState(false);
  const [showModalEstrategia, setShowModalEstrategia]   = useState(false);
  const [estrategiaOrigem, setEstrategiaOrigem]         = useState('venda'); // venda | expansao
  // Decisões de expansão: { cliente_empresa: 'tentar_venda' | 'nao_relevante' }
  const [decisoes, setDecisoes]                   = useState({});
  const [decidindo, setDecidindo]                 = useState(null);
  const [filtroExpansao, setFiltroExpansao]       = useState('todas');
  const [showInfoRisco, setShowInfoRisco]         = useState(false);
  const [showInfoChance, setShowInfoChance]       = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('access');
    fetch(`${API}/api/reunioes/oportunidades/`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setDados(d);
          setDecisoes(d.expansao_decisoes || {});
        } else setErro(d.error || 'Erro ao carregar dados');
      })
      .catch(e => setErro(String(e)))
      .finally(() => setCarregando(false));
  }, []);

  // Marca a decisão sobre uma expansão (clicar de novo desfaz → pendente)
  const decidirExpansao = async (clienteEmpresa, decisao) => {
    const chave = (clienteEmpresa || '').trim();
    if (!chave) return;
    const anterior = decisoes[chave] || 'pendente';
    const nova = anterior === decisao ? 'pendente' : decisao;
    setDecidindo(chave);
    setDecisoes(prev => {
      const n = { ...prev };
      if (nova === 'pendente') delete n[chave]; else n[chave] = nova;
      return n;
    });
    try {
      const token = localStorage.getItem('access');
      const res = await fetch(`${API}/api/reunioes/expansao-decisao/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ cliente_empresa: chave, decisao: nova }),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.erro || 'erro');
    } catch {
      setDecisoes(prev => {
        const n = { ...prev };
        if (anterior === 'pendente') delete n[chave]; else n[chave] = anterior;
        return n;
      });
    } finally {
      setDecidindo(null);
    }
  };

  const oportunidades    = dados?.oportunidades         || [];
  const objecoesFreq     = dados?.objecoes_freq          || [];
  const todasExpansaoRaw = dados?.oportunidades_expansao || [];

  const oportunidadesPorCliente = useMemo(() => {
    const grupos = {};
    oportunidades.forEach(op => {
      const chave = op.cliente_empresa?.trim() || `__sem_empresa__${op.codigo_reuniao}`;
      if (!grupos[chave]) grupos[chave] = [];
      grupos[chave].push(op);
    });
    return Object.entries(grupos).map(([chave, reunioes]) => {
      const sorted = [...reunioes].sort((a, b) => {
        const oA = ETAPAS.find(e => e.key === normStage(a.estagio_venda))?.ordem ?? 0;
        const oB = ETAPAS.find(e => e.key === normStage(b.estagio_venda))?.ordem ?? 0;
        if (oB !== oA) return oB - oA;
        return (b.data_reuniao || '').localeCompare(a.data_reuniao || '');
      });
      const principal = sorted[0];
      const recente = [...reunioes].sort((a, b) =>
        (b.data_reuniao || '').localeCompare(a.data_reuniao || '')
      )[0];
      const alertas = [...new Set(reunioes.flatMap(r => r.alertas || []))];
      const todasObjecoes = reunioes.flatMap(r => r.objecoes || []);
      const melhorInteresse = Math.max(...reunioes.map(r => interesseNum(r.interesse_nivel)));
      const produtos = [...new Set(reunioes.flatMap(r => [
        ...(Array.isArray(r.produtos_servicos) ? r.produtos_servicos : []),
        ...(r.produto ? [r.produto] : []),
      ]).filter(Boolean).map(p => String(p).trim()))];
      return {
        ...principal,
        estagio_venda: estagioOverrides[chave] || principal.estagio_venda,
        alertas,
        objecoes: todasObjecoes,
        produtos,
        proximos_passos: recente.proximos_passos || [],
        interesse_nivel: melhorInteresse || interesseNum(principal.interesse_nivel),
        data_reuniao: recente.data_reuniao,
        dias_desde_reuniao: recente.dias_desde_reuniao,
        nome_operador: recente.nome_operador,
        total_reunioes: reunioes.length,
        _chave: chave,
        _reunioes: [...reunioes].sort((a, b) =>
          (b.data_reuniao || '').localeCompare(a.data_reuniao || '')
        ),
      };
    });
  }, [oportunidades, estagioOverrides]);

  // Pipeline completo: oportunidades comerciais + expansões marcadas como "tentar venda"
  // (clientes de expansão que ainda não estavam no funil entram como Lead)
  const pipelineCompleto = useMemo(() => {
    const existentes = new Set(
      oportunidadesPorCliente
        .map(o => (o.cliente_empresa || '').trim().toLowerCase())
        .filter(Boolean)
    );
    const grupos = {};
    todasExpansaoRaw.forEach(r => {
      const nome = (r.cliente_empresa || '').trim();
      if (!nome) return;
      if (decisoes[nome] !== 'tentar_venda') return;
      if (existentes.has(nome.toLowerCase())) return;
      if (!grupos[nome]) grupos[nome] = [];
      grupos[nome].push(r);
    });
    const extras = Object.entries(grupos).map(([nome, reunioes]) => {
      const ordenadas = [...reunioes].sort((a, b) =>
        (b.data_reuniao || '').localeCompare(a.data_reuniao || '')
      );
      const recente = ordenadas[0];
      let dias = 0;
      if (recente.data_reuniao) {
        const dt = new Date(String(recente.data_reuniao).slice(0, 10) + 'T00:00:00');
        if (!isNaN(dt)) dias = Math.max(0, Math.floor((Date.now() - dt.getTime()) / 86400000));
      }
      return {
        ...recente,
        estagio_venda: estagioOverrides[nome] || recente.estagio_venda || 'Lead',
        alertas: [],
        objecoes: [],
        proximos_passos: recente.proximos_passos || [],
        produtos: [...new Set(reunioes.flatMap(r => [
          ...(Array.isArray(r.produtos_servicos) ? r.produtos_servicos : []),
          ...(r.produto ? [r.produto] : []),
        ]).filter(Boolean).map(p => String(p).trim()))],
        interesse_nivel: interesseNum(recente.interesse_nivel),
        dias_desde_reuniao: dias,
        total_reunioes: reunioes.length,
        _chave: nome,
        _reunioes: ordenadas,
        _origemExpansao: true,
      };
    });
    return [...oportunidadesPorCliente, ...extras];
  }, [oportunidadesPorCliente, todasExpansaoRaw, decisoes, estagioOverrides]);

  const atualizarEstagio = async (chave, clienteEmpresa, estagio) => {
    setAtualizando(chave);
    setEstagioOverrides(prev => ({ ...prev, [chave]: estagio }));
    try {
      const token = localStorage.getItem('access');
      await fetch(`${API}/api/reunioes/atualizar-estagio/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ cliente_empresa: clienteEmpresa, estagio_venda: estagio }),
      });
    } catch {
      setEstagioOverrides(prev => { const n = { ...prev }; delete n[chave]; return n; });
    } finally {
      setAtualizando(null);
    }
  };

  const funil = useMemo(() => {
    const counts = {};
    const countsExp = {};
    ETAPAS.forEach(e => { counts[e.key] = 0; countsExp[e.key] = 0; });
    pipelineCompleto.forEach(op => {
      const k = normStage(op.estagio_venda);
      counts[k] = (counts[k] || 0) + 1;
      if (op._origemExpansao) countsExp[k] = (countsExp[k] || 0) + 1;
    });
    return ETAPAS.map(e => ({ ...e, count: counts[e.key] || 0, countExp: countsExp[e.key] || 0 }));
  }, [pipelineCompleto]);

  const kpis = useMemo(() => {
    const quentes  = pipelineCompleto.filter(o => interesseNum(o.interesse_nivel) >= 7).length;
    const em_risco = pipelineCompleto.filter(o => (o.alertas || []).length > 0).length;
    const avancadas = pipelineCompleto.filter(o => ['proposta','negociacao','finalizado'].includes(normStage(o.estagio_venda))).length;
    return { total: pipelineCompleto.length, quentes, em_risco, avancadas };
  }, [pipelineCompleto]);

  const filtradas = useMemo(() => pipelineCompleto.filter(op => {
    if (filtroEtapa !== 'todas' && normStage(op.estagio_venda) !== filtroEtapa) return false;
    if (filtroAlerta !== 'todos' && !(op.alertas || []).includes(filtroAlerta)) return false;
    if (busca) {
      const b = busca.toLowerCase();
      if (!((op.cliente_empresa || '').toLowerCase().includes(b) ||
            (op.assunto || '').toLowerCase().includes(b) ||
            (op.nome_operador || '').toLowerCase().includes(b))) return false;
    }
    return true;
  }), [pipelineCompleto, filtroEtapa, filtroAlerta, busca]);

  const emRisco = useMemo(() =>
    pipelineCompleto
      .filter(o => (o.alertas || []).length > 0)
      .sort((a, b) => (b.alertas?.length || 0) - (a.alertas?.length || 0))
      .slice(0, 8),
    [pipelineCompleto]
  );

  const upsellCrossell = useMemo(() => {
    const norm = s => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    const upKw = ['upsell','upgrade','ampliar','expandir','crescimento','escalar','escalabilidade','mais usuarios','mais modulos','mais servicos','assumir','ambos os servicos','todos os servicos','plano superior','plano maior','plano premium','aumentar','insatisfacao','insatisfeito','trocar','substituir','migrar','provedor atual','fornecedor atual','potencial de crescimento','aumento','plano avancado'];
    const crossKw = ['cross','outro produto','outra solucao','solucao adicional','solucao complementar','modulo adicional','produto adicional','novo produto','novo modulo','integracao','parceria','indicacao','network','benchmarks','troca de experiencias','outros clientes','alem do','tambem precisa','tambem interessa'];
    const grupos = {};
    todasExpansaoRaw.forEach(r => {
      const chave = (r.cliente_empresa || '').trim() || `__sem__${r.codigo_reuniao}`;
      if (!grupos[chave]) grupos[chave] = [];
      grupos[chave].push(r);
    });
    return Object.entries(grupos).map(([chave, reunioes]) => {
      const recente = reunioes[0];
      const txtCompleto = reunioes.map(r => r.oportunidade || '').join(' ');
      const txt = norm(txtCompleto);
      const isUp    = upKw.some(k => txt.includes(k));
      const isCross = crossKw.some(k => txt.includes(k));
      const tipo = isUp && isCross ? 'ambos' : isUp ? 'upsell' : isCross ? 'crosssell' : 'oportunidade';
      return { ...recente, oportunidade: txtCompleto, total_reunioes: reunioes.length, _chave: chave, _reunioes: reunioes, _tipoExpansao: tipo };
    }).sort((a, b) => interesseNum(b.interesse_nivel) - interesseNum(a.interesse_nivel));
  }, [todasExpansaoRaw]);

  // Objeções agrupadas por categoria (para o gráfico ser legível)
  const objecoesPorCategoria = useMemo(() => {
    const mapa = {};
    objecoesFreq.forEach(o => {
      const cat = categorizarObjecao(o.texto);
      if (!mapa[cat.key]) mapa[cat.key] = { ...cat, count: 0 };
      mapa[cat.key].count += o.count || 1;
    });
    return Object.values(mapa).sort((a, b) => b.count - a.count);
  }, [objecoesFreq]);

  // Funil só das expansões marcadas como "tentar venda" (upsell/cross-sell)
  const funilExpansao = useMemo(() => {
    const counts = {};
    ETAPAS.forEach(e => { counts[e.key] = 0; });
    upsellCrossell.forEach(op => {
      const nome = (op.cliente_empresa || '').trim();
      if ((decisoes[nome] || 'pendente') !== 'tentar_venda') return;
      const entrada = pipelineCompleto.find(p =>
        (p.cliente_empresa || '').trim().toLowerCase() === nome.toLowerCase()
      );
      const k = normStage(estagioOverrides[nome] || entrada?.estagio_venda || op.estagio_venda || 'Lead');
      counts[k] = (counts[k] || 0) + 1;
    });
    return ETAPAS.map(e => ({ ...e, count: counts[e.key] || 0 }));
  }, [upsellCrossell, decisoes, pipelineCompleto, estagioOverrides]);

  const totalFunilExpansao = funilExpansao.reduce((s, e) => s + e.count, 0);

  const proximasAcoes = useMemo(() => {
    const acoes = [];
    pipelineCompleto.forEach(op => {
      (op.proximos_passos || []).slice(0, 1).forEach(p => {
        const txt = textoProximo(p);
        if (txt) acoes.push({ txt, operador: op.nome_operador, empresa: op.cliente_empresa });
      });
    });
    return acoes.slice(0, 10);
  }, [pipelineCompleto]);

  // ── Narração em áudio (Gemini TTS) — mesmo padrão do Painel do Operador ──
  const [audioPartes, setAudioPartes] = useState([]);
  const [totalPartes, setTotalPartes] = useState(0);
  const [parteAtual, setParteAtual]   = useState(0);
  const [gerandoAudio, setGerandoAudio] = useState(false);
  const [velocAudio, setVelocAudio]   = useState(1);
  const audioRef = useRef(null);

  // 1ª parte curta (~600 chars) pra voz começar em segundos; demais maiores
  const dividirEmPartes = (texto) => {
    const blocos = [];
    texto.split(/\n{2,}/).forEach(par => {
      if (par.length <= 600) { blocos.push(par); return; }
      let frase = '';
      par.split(/(?<=[.!?:])\s+/).forEach(fr => {
        if (frase && (frase.length + fr.length) > 600) { blocos.push(frase); frase = fr; }
        else frase = frase ? `${frase} ${fr}` : fr;
      });
      if (frase) blocos.push(frase);
    });
    const partes = [];
    let atual = '';
    blocos.forEach(b => {
      const limite = partes.length === 0 ? 600 : 1800;
      if (atual && (atual.length + b.length) > limite) { partes.push(atual); atual = b; }
      else atual = atual ? `${atual}\n\n${b}` : b;
    });
    if (atual.trim()) partes.push(atual);
    return partes;
  };

  const gerarAudio = (texto) => {
    if (!texto || gerandoAudio) return;
    const partes = dividirEmPartes(texto);
    if (!partes.length) return;
    setGerandoAudio(true);
    setTotalPartes(partes.length);
    setAudioPartes(new Array(partes.length).fill(null));
    setParteAtual(0);
    const token = localStorage.getItem('access');
    partes.forEach((textoParte, i) => {
      fetch(`${API}/api/reunioes/gerar-audio/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ texto: textoParte }),
      }).then(r => r.json()).then(data => {
        if (data.success) {
          setAudioPartes(prev => {
            const c = [...prev];
            c[i] = `data:${data.mime};base64,${data.audio}`;
            return c;
          });
        } else if (i === 0) {
          notificar('' + (data.erro || 'Erro ao gerar o áudio.'));
          setGerandoAudio(false);
          setTotalPartes(0);
        }
      }).catch(err => {
        if (i === 0) {
          notificar('' + err.message);
          setGerandoAudio(false);
          setTotalPartes(0);
        }
      });
    });
  };

  useEffect(() => {
    if (gerandoAudio && audioPartes[0]) setGerandoAudio(false);
  }, [audioPartes, gerandoAudio]);

  const mudarVelocidade = (v) => {
    setVelocAudio(v);
    if (audioRef.current) audioRef.current.playbackRate = v;
  };

  const renderBarraAudio = (texto) => {
    if (!texto || texto.startsWith('')) return null;
    return (
      <div className="p-3 rounded-3 mb-3 d-flex align-items-center gap-3 flex-wrap"
        style={{ background: '#f0faf5', border: '1px solid #badbcc' }}>
        {totalPartes === 0 || gerandoAudio ? (
          <>
            <button
              className="btn btn-sm fw-semibold"
              onClick={() => gerarAudio(texto)}
              disabled={gerandoAudio}
              style={{
                background: 'linear-gradient(135deg, #198754 0%, #20c997 100%)',
                border: 'none', color: '#fff',
              }}
            >
              {gerandoAudio
                ? <><span className="spinner-border spinner-border-sm me-2"></span>Deixa comigo, já vou falar...</>
                : <><i className="fas fa-volume-up me-1"></i>Quer que eu fale pra você?</>}
            </button>
            <small className="text-muted">
              {gerandoAudio
                ? 'Só um instante, estou organizando as ideias — começo a falar em segundos. '
                : 'Sem tempo de ler? Sou eu, a Memor.IA — aperta aí que eu te conto a estratégia. '}
            </small>
          </>
        ) : audioPartes[parteAtual] ? (
          <>
            <audio
              ref={audioRef}
              key={parteAtual}
              src={audioPartes[parteAtual]}
              controls
              autoPlay
              style={{ height: 38, flexGrow: 1, minWidth: 240 }}
              onLoadedMetadata={() => { if (audioRef.current) audioRef.current.playbackRate = velocAudio; }}
              onEnded={() => {
                if (parteAtual + 1 < totalPartes) setParteAtual(p => p + 1);
              }}
            ></audio>
            {totalPartes > 1 && (
              <span className="badge bg-secondary">Parte {parteAtual + 1}/{totalPartes}</span>
            )}
            <div className="btn-group btn-group-sm">
              {[1, 1.25, 1.5].map(v => (
                <button
                  key={v}
                  className={`btn ${velocAudio === v ? '' : 'btn-outline-secondary'}`}
                  style={velocAudio === v ? { backgroundColor: '#198754', borderColor: '#198754', color: '#fff' } : undefined}
                  onClick={() => mudarVelocidade(v)}
                >
                  {v}x
                </button>
              ))}
            </div>
          </>
        ) : (
          <small className="text-muted">
            <span className="spinner-border spinner-border-sm me-2"></span>
            Pegando fôlego para a parte {parteAtual + 1}/{totalPartes}... 
          </small>
        )}
      </div>
    );
  };

  const pedirEstrategia = async (op) => {
    // Upsell/cross-sell (cliente da base) usa estratégia de EXPANSÃO, não de lead frio
    const ehExpansao = !!(op._origemExpansao || op._tipoExpansao
      || decisoes[(op.cliente_empresa || '').trim()] === 'tentar_venda');
    setEstrategiaOrigem(ehExpansao ? 'expansao' : 'venda');
    setEstrategia('');
    setShowModalEstrategia(true);
    setCarregandoEstrategia(true);
    setAudioPartes([]);
    setTotalPartes(0);
    setParteAtual(0);
    setVelocAudio(1);
    try {
      const token = localStorage.getItem('access');
      const res = await fetch(`${API}/api/reunioes/estrategia-fechamento/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          cliente_empresa: op.cliente_empresa,
          reunioes: op._reunioes,
          origem: ehExpansao ? 'expansao' : 'venda',
        }),
      });
      const d = await res.json();
      if (d.success) setEstrategia(d.estrategia);
      else setEstrategia('' + (d.erro || 'Erro ao gerar estratégia.'));
    } catch (e) {
      setEstrategia('' + String(e));
    } finally {
      setCarregandoEstrategia(false);
    }
  };

  const renderMarkdown = (texto) => {
    if (!texto) return null;
    const linhas = texto.split('\n');
    const resultado = [];
    let listaAtual = [];
    const flushLista = () => {
      if (listaAtual.length > 0) {
        resultado.push(<ul key={`ul-${resultado.length}`} style={{ paddingLeft: 20, marginBottom: 12 }}>{listaAtual}</ul>);
        listaAtual = [];
      }
    };
    const renderInline = (text, key) => {
      const partes = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
      return (
        <span key={key}>
          {partes.map((p, i) => {
            if (p.startsWith('**') && p.endsWith('**')) return <strong key={i}>{p.slice(2, -2)}</strong>;
            if (p.startsWith('*')  && p.endsWith('*'))  return <em key={i} style={{ color: '#555' }}>{p.slice(1, -1)}</em>;
            return p;
          })}
        </span>
      );
    };
    linhas.forEach((linha, i) => {
      if (linha.startsWith('## ')) {
        flushLista();
        resultado.push(<h5 key={i} className="fw-bold mt-4 mb-2 pb-2" style={{ borderBottom: '2px solid #dee2e6', fontSize: '1.2rem' }}>{linha.slice(3)}</h5>);
      } else if (linha.startsWith('### ')) {
        flushLista();
        resultado.push(<h6 key={i} className="fw-bold mt-3 mb-1" style={{ color: '#6610f2', fontSize: '1.05rem' }}>{linha.slice(4)}</h6>);
      } else if (/^---+$/.test(linha.trim())) {
        flushLista();
        resultado.push(<hr key={i} className="my-3" />);
      } else if (/^[\*\-]\s+/.test(linha)) {
        listaAtual.push(<li key={i} style={{ marginBottom: 6, lineHeight: 1.7, fontSize: '1rem' }}>{renderInline(linha.replace(/^[\*\-]\s+/, ''), `li-${i}`)}</li>);
      } else if (/^\d+\.\s+/.test(linha)) {
        flushLista();
        const num = linha.match(/^(\d+)\.\s+(.*)/);
        if (num) resultado.push(
          <div key={i} className="d-flex gap-2 mb-2 align-items-start">
            <span className="badge rounded-pill" style={{ background: '#6610f2', minWidth: 24, fontSize: '0.85rem', flexShrink: 0, marginTop: 3 }}>{num[1]}</span>
            <span style={{ fontSize: '1rem', lineHeight: 1.7 }}>{renderInline(num[2], `num-${i}`)}</span>
          </div>
        );
      } else if (linha.trim() === '') {
        flushLista();
        resultado.push(<div key={i} style={{ height: 8 }} />);
      } else {
        flushLista();
        resultado.push(<p key={i} className="mb-1" style={{ fontSize: '1rem', lineHeight: 1.8 }}>{renderInline(linha, `p-${i}`)}</p>);
      }
    });
    flushLista();
    return resultado;
  };

  if (carregando) return (
    <div className="das-page">
      <div className="container-fluid mt-5 text-center">
        <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }}></div>
        <p className="mt-3 text-muted">Carregando pipeline comercial...</p>
      </div>
    </div>
  );

  if (erro) return (
    <div className="das-page">
      <div className="container-fluid mt-5 text-center">
        <div className="alert alert-danger d-inline-block">{erro}</div>
        <div>
          <button className="btn btn-primary mt-2" onClick={() => window.location.reload()}>Tentar novamente</button>
        </div>
      </div>
    </div>
  );

  const totalFunil = Math.max(funil.reduce((s, e) => s + e.count, 0), 1);

  return (
    <div className="das-page">
      <div className="container-fluid mt-4">

        {/* ── MODAL REUNIÕES DO CLIENTE ── */}
        {modalReunioes && (
          <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }} onClick={() => setModalReunioes(null)}>
            <div className="modal-dialog modal-dialog-scrollable" style={{ maxWidth: 760 }} onClick={e => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                      <i className="fas fa-building me-2"></i>{modalReunioes.cliente_empresa || 'Sem empresa'}
                    </h5>
                    <small style={{ color: '#6c757d' }}>
                      {modalReunioes._reunioes.length} reunião{modalReunioes._reunioes.length !== 1 ? 'ões' : ''} registrada{modalReunioes._reunioes.length !== 1 ? 's' : ''}
                    </small>
                  </div>
                  <div className="d-flex gap-2 align-items-center">
                    <button
                      className="btn btn-sm fw-semibold"
                      onClick={() => { setModalReunioes(null); pedirEstrategia(modalReunioes); }}
                      style={{
                        background: 'linear-gradient(135deg, #198754 0%, #20c997 100%)',
                        border: 'none', color: '#fff',
                        boxShadow: '0 2px 8px rgba(25,135,84,0.3)',
                      }}
                    >
                      Como fechar essa venda?
                    </button>
                    <button className="btn-close" onClick={() => setModalReunioes(null)}></button>
                  </div>
                </div>
                <div className="modal-body" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                  {modalReunioes._reunioes.map((r, i) => {
                    const etapaR = ETAPAS.find(e => e.key === normStage(r.estagio_venda)) || ETAPAS[0];
                    return (
                      <div key={r.codigo_reuniao || i} className="card border mb-3">
                        <div className="card-body p-3">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                              <strong className="d-block text-truncate">{r.assunto || '—'}</strong>
                              <small className="text-muted">{r.nome_operador} • {r.data_reuniao || '—'}</small>
                            </div>
                            <div className="d-flex gap-1 flex-shrink-0 ms-2">
                              <span className="badge" style={{ background: etapaR.cor, fontSize: '0.72rem' }}>{etapaR.label}</span>
                              <span className={`badge ${badgeNota(r.interesse_nivel)}`} style={{ fontSize: '0.72rem' }}>{r.interesse_nivel || 0}/10</span>
                            </div>
                          </div>
                          {r.resumo && (
                            <p className="mb-2 p-2 rounded" style={{ background: '#f8f9fa', fontSize: '0.83rem' }}>{r.resumo}</p>
                          )}
                          <div className="row g-2">
                            {r.oportunidade && (
                              <div className="col">
                                <div className="alert alert-success py-2 px-3 mb-0">
                                  <strong style={{ fontSize: '0.75rem' }}>OPORTUNIDADE</strong>
                                  <p className="mb-0 mt-1" style={{ fontSize: '0.82rem' }}>{r.oportunidade}</p>
                                </div>
                              </div>
                            )}
                            {r.pontos_criticos && (
                              <div className="col">
                                <div className="alert alert-danger py-2 px-3 mb-0">
                                  <strong style={{ fontSize: '0.75rem' }}>RISCO</strong>
                                  <p className="mb-0 mt-1" style={{ fontSize: '0.82rem' }}>{r.pontos_criticos}</p>
                                </div>
                              </div>
                            )}
                          </div>
                          {(r.proximos_passos || []).length > 0 && (
                            <div className="mt-2">
                              <small className="text-muted fw-semibold">PRÓXIMOS PASSOS</small>
                              {r.proximos_passos.map((p, pi) => (
                                <p key={pi} className="mb-0" style={{ fontSize: '0.83rem' }}>→ {textoProximo(p)}</p>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setModalReunioes(null)}>Fechar</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── MODAL: O QUE É UMA OPORTUNIDADE EM RISCO ── */}
        {showInfoRisco && (
          <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }} onClick={() => setShowInfoRisco(false)}>
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-exclamation-triangle me-2 text-danger"></i>
                    O que é uma oportunidade em risco?
                  </h5>
                  <button className="btn-close" onClick={() => setShowInfoRisco(false)}></button>
                </div>
                <div className="modal-body">
                  <p className="text-muted">
                    Uma oportunidade entra em risco quando a Memor.IA identifica, nas reuniões e no
                    tempo de resposta, sinais de que a venda pode esfriar ou ser perdida.
                    Os alertas são:
                  </p>
                  {[
                    { icon: '', titulo: 'Sem follow-up', texto: 'A reunião terminou sem próximos passos definidos e já se passaram mais de 7 dias sem um novo contato.' },
                    { icon: '', titulo: '+10 dias parada', texto: 'Há mais de 10 dias sem nenhuma reunião com esse cliente — a negociação está esfriando.' },
                    { icon: '', titulo: 'Muitas objeções', texto: 'O cliente levantou mais de 2 objeções (preço, prazo, concorrente...) que precisam ser tratadas.' },
                    { icon: '', titulo: 'Baixo interesse', texto: 'A Memor.IA avaliou o interesse do cliente em 3/10 ou menos na conversa.' },
                  ].map((a, i) => (
                    <div key={i} className="d-flex gap-3 mb-3">
                      <div style={{ fontSize: '1.3rem', flexShrink: 0 }}>{a.icon}</div>
                      <div>
                        <strong style={{ fontSize: '0.92rem' }}>{a.titulo}</strong>
                        <p className="mb-0 text-muted" style={{ fontSize: '0.87rem' }}>{a.texto}</p>
                      </div>
                    </div>
                  ))}
                  <div className="alert alert-light border mb-0" style={{ fontSize: '0.85rem' }}>
                    <i className="fas fa-lightbulb me-2 text-warning"></i>
                    Quanto mais alertas uma oportunidade acumula, mais alto ela aparece na lista.
                    Use o botão <strong>"Como fechar essa venda?"</strong> para a Memor.IA sugerir a
                    estratégia de recuperação.
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowInfoRisco(false)}>
                    <i className="fas fa-times me-1"></i>Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── MODAL INFO CHANCE DE FECHAR ── */}
        {showInfoChance && (
          <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }} onClick={() => setShowInfoChance(false)}>
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-percentage me-2 text-success"></i>
                    Como a chance de fechar é calculada?
                  </h5>
                  <button className="btn-close" onClick={() => setShowInfoChance(false)}></button>
                </div>
                <div className="modal-body">
                  <p className="text-muted">
                    O cálculo combina os sinais que a Memor.IA já extraiu das reuniões daquele cliente:
                  </p>
                  {[
                    { icon: '', titulo: 'Soma', texto: 'Interesse do cliente (peso maior), etapa avançada no funil (Qualificação +12, Proposta +24, Negociação +36) e relacionamento ativo (2 ou mais reuniões).' },
                    { icon: '', titulo: 'Desconta', texto: 'Objeções levantadas, alertas ativos (sem follow-up, baixo interesse...) e tempo parada (mais de 7 ou 15 dias sem reunião).' },
                    { icon: '', titulo: 'Casos especiais', texto: 'Finalizado = 100% "Fechada" e Perdido = 0% "Perdida".' },
                  ].map((a, i) => (
                    <div key={i} className="d-flex gap-3 mb-3">
                      <div style={{ fontSize: '1.3rem', flexShrink: 0 }}>{a.icon}</div>
                      <div>
                        <strong style={{ fontSize: '0.92rem' }}>{a.titulo}</strong>
                        <p className="mb-0 text-muted" style={{ fontSize: '0.87rem' }}>{a.texto}</p>
                      </div>
                    </div>
                  ))}
                  <div className="alert alert-light border mb-0" style={{ fontSize: '0.85rem' }}>
                    <i className="fas fa-lightbulb me-2 text-success"></i>
                    A chance recalcula na hora quando você move o cliente de etapa no funil.
                    Em <strong>"Ver detalhes"</strong> aparece a lista exata do que pesou no percentual daquele cliente.
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowInfoChance(false)}>
                    <i className="fas fa-times me-1"></i>Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── MODAL ESTRATÉGIA IA ── */}
        {showModalEstrategia && (
          <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1200 }} onClick={() => setShowModalEstrategia(false)}>
            <div className="modal-dialog modal-dialog-scrollable" style={{ maxWidth: '95vw', width: 1200, margin: '1rem auto' }} onClick={e => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                      {estrategiaOrigem === 'expansao'
                        ? 'Estratégia de Expansão (Upsell/Cross-sell) — Memor.IA'
                        : 'Estratégia de Fechamento — Memor.IA'}
                    </h5>
                    <small style={{ color: '#6c757d' }}>
                      {estrategiaOrigem === 'expansao'
                        ? 'Cliente da base — estratégia para expandir a conta sem desgastar a relação'
                        : 'Análise personalizada com base no histórico real de reuniões'}
                    </small>
                  </div>
                  <button className="btn-close" onClick={() => setShowModalEstrategia(false)}></button>
                </div>
                <div className="modal-body" style={{ maxHeight: '80vh', overflowY: 'auto', padding: '1.8rem 2.4rem' }}>
                  {carregandoEstrategia ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-success mb-3" style={{ width: '2.5rem', height: '2.5rem' }}></div>
                      <p className="text-muted fw-semibold">A Memor.IA está analisando o histórico e montando sua estratégia...</p>
                      <small className="text-muted">Isso pode levar alguns segundos</small>
                    </div>
                  ) : (
                    <div>
                      {renderBarraAudio(estrategia)}
                      {renderMarkdown(estrategia)}
                    </div>
                  )}
                </div>
                {!carregandoEstrategia && (
                  <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={() => setShowModalEstrategia(false)}>Fechar</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── HEADER ── */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <div className="mb-4">
                      <span className="badge bg-light text-dark me-2" style={{ fontSize: '20px', fontWeight: '500' }}>
                        Oportunidades Comerciais — Memor.IA
                      </span>
                      <span className="badge" style={{ fontSize: '15px', fontWeight: '500', backgroundColor: '#00205b', color: '#fff' }}>
                        <i className="fas fa-funnel-dollar me-1"></i>Pipeline Inteligente
                      </span>
                    </div>
                    <div className="d-flex align-items-center mb-2">
                      <i className="fas fa-bullseye header-icon me-2"></i>
                      <h2 className="titulo-pagina m-0">Pipeline de Vendas</h2>
                    </div>
                    <p className="mb-1 opacity-75">
                      <i className="fas fa-robot me-2"></i>
                      Acompanhe seus leads, oportunidades e estratégias de fechamento com a Memor.IA
                    </p>
                    <small className="opacity-75">
                      {kpis.total} oportunidade{kpis.total !== 1 ? 's' : ''} · {kpis.quentes} quente{kpis.quentes !== 1 ? 's' : ''} · {kpis.avancadas} em fase avançada
                    </small>
                  </div>
                  <div className="mb-2">
                    <button
                      className="btn btn-sm"
                      onClick={() => navigate('/analise-gestor-memora')}
                      style={{ backgroundColor: '#00205b', borderColor: '#00205b', color: '#fff' }}
                    >
                      <i className="fas fa-arrow-left me-1"></i>Voltar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── KPIs ── */}
        <div className="row g-3 mb-4">
          {[
            { icon: 'fa-clipboard-list', cor: '#00205b', valor: kpis.total,     label: 'Total no Pipeline',  sub: 'oportunidades' },
            { icon: 'fa-fire',           cor: '#fd7e14', valor: kpis.quentes,   label: 'Quentes',            sub: 'interesse ≥ 7/10' },
            { icon: 'fa-exclamation-triangle', cor: '#dc3545', valor: kpis.em_risco, label: 'Em Risco', sub: 'precisam atenção' },
            { icon: 'fa-rocket',         cor: '#198754', valor: kpis.avancadas, label: 'Fase Avançada',      sub: 'proposta / negoc. / fecha.' },
          ].map((k, i) => (
            <div key={i} className="col-6 col-md-3">
              <div className="card border-0 shadow-sm h-100" style={{ borderTop: `3px solid ${k.cor}` }}>
                <div className="card-body d-flex align-items-center gap-2 py-3 px-3">
                  <div style={{ background: `${k.cor}1a`, borderRadius: 12, width: 46, height: 46, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <i className={`fas ${k.icon}`} style={{ color: k.cor, fontSize: '1.2rem' }}></i>
                  </div>
                  <div>
                    <div className="fw-bold lh-1 mb-1" style={{ fontSize: '1.5rem', color: k.cor }}>{k.valor}</div>
                    <div className="fw-semibold text-dark" style={{ fontSize: '0.73rem' }}>{k.label}</div>
                    <div className="text-muted" style={{ fontSize: '0.67rem' }}>{k.sub}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── FUNIL + EM RISCO ── */}
        <div className="row g-4 mb-4">
          <div className="col-md-7">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white">
                <h6 className="mb-0"><i className="fas fa-filter me-2 text-primary"></i>Funil de Vendas</h6>
                <small className="text-muted">Clique em uma etapa para filtrar</small>
              </div>
              <div className="card-body">
                {funil.map((etapa, i) => {
                  // Preenchimento estilo barra de progresso: proporcional à quantidade,
                  // com escala mínima de 10 para o cinza restante dar a sensação de
                  // espaço a preencher (igual ao Planejamento Orçamentário)
                  const escala = Math.max(totalFunil, 10);
                  const pct = etapa.count > 0
                    ? Math.max((etapa.count / escala) * 100, 5)
                    : 0;
                  const indent = i * 3;
                  const ativo = filtroEtapa === etapa.key;
                  return (
                    <div key={etapa.key} className="d-flex align-items-center gap-3 mb-2" style={{ cursor: 'pointer' }}
                      onClick={() => setFiltroEtapa(ativo ? 'todas' : etapa.key)}>
                      <div style={{ width: 110, textAlign: 'right', flexShrink: 0 }}>
                        <span className="fw-semibold" style={{ fontSize: '0.85rem', color: ativo ? etapa.cor : '#6c757d' }}>{etapa.label}</span>
                      </div>
                      <div style={{ flex: 1, position: 'relative', height: 32 }}>
                        {/* trilho cinza (o "faltando") */}
                        <div style={{
                          position: 'absolute', left: `${indent}%`, right: `${indent}%`, top: 0, bottom: 0,
                          background: '#e9ecef', borderRadius: 6,
                        }} />
                        {etapa.count > 0 && (() => {
                          const larguraTotal = pct * (100 - indent * 2) / 100;
                          const base = etapa.count - etapa.countExp;
                          const wBase = larguraTotal * (base / etapa.count);
                          const wExp = larguraTotal - wBase;
                          return (
                            <>
                              {base > 0 && (
                                <div style={{
                                  position: 'absolute', left: `${indent}%`, width: `${wBase}%`,
                                  top: 0, bottom: 0, background: etapa.cor,
                                  borderRadius: '6px 0 0 6px', transition: 'width 0.5s ease',
                                  opacity: ativo ? 1 : 0.85,
                                }} />
                              )}
                              {etapa.countExp > 0 && (
                                <div
                                  title={`${etapa.countExp} vinda(s) de Upsell/Cross-sell`}
                                  style={{
                                    position: 'absolute', left: `${indent + wBase}%`, width: `${wExp}%`,
                                    top: 0, bottom: 0, background: '#6610f2',
                                    borderRadius: base > 0 ? '0' : '6px 0 0 6px',
                                    transition: 'width 0.5s ease',
                                    opacity: ativo ? 1 : 0.85,
                                  }} />
                              )}
                            </>
                          );
                        })()}
                      </div>
                      <span className="fw-bold" style={{ width: 24, textAlign: 'right', fontSize: '0.9rem', color: '#343a40' }}>{etapa.count}</span>
                    </div>
                  );
                })}
                {funil.some(e => e.countExp > 0) && (
                  <div className="d-flex align-items-center gap-2 mt-2">
                    <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: '#6610f2' }}></span>
                    <small className="text-muted">Vindas de Upsell / Cross-sell (expansão)</small>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-md-5">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white d-flex justify-content-between align-items-center" style={{ borderBottom: '2px solid #dc3545' }}>
                <h6 className="mb-0">
                  <i className="fas fa-exclamation-triangle me-2 text-danger"></i>Oportunidades em Risco
                  {emRisco.length > 0 && <span className="badge bg-danger ms-2">{emRisco.length}</span>}
                </h6>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setShowInfoRisco(true)}
                  title="Entenda os critérios de risco"
                >
                  <i className="fas fa-question-circle me-1"></i>O que é risco?
                </button>
              </div>
              <div className="card-body p-0" style={{ maxHeight: 320, overflowY: 'auto' }}>
                {emRisco.length === 0 ? (
                  <div className="text-center py-4 text-success">
                    <i className="fas fa-check-circle fa-2x mb-2 d-block"></i>
                    <small>Nenhuma oportunidade em risco.</small>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Cliente</th>
                          <th>Há</th>
                          <th>Alertas</th>
                        </tr>
                      </thead>
                      <tbody>
                        {emRisco.map(op => (
                          <tr key={op._chave}>
                            <td><strong style={{ fontSize: '0.83rem' }}>{op.cliente_empresa || op.assunto || '—'}</strong></td>
                            <td><span className={`badge ${op.dias_desde_reuniao > 10 ? 'bg-danger' : 'bg-secondary'}`}>{op.dias_desde_reuniao}d</span></td>
                            <td>
                              <div className="d-flex flex-wrap gap-1">
                                {(op.alertas || []).map(a => (
                                  <span key={a} className={`badge bg-${ALERT_INFO[a]?.cls || 'secondary'} bg-opacity-10 border`} style={{ fontSize: '0.7rem', color: 'inherit' }}>
                                    {ALERT_INFO[a]?.icon} {ALERT_INFO[a]?.label || a}
                                  </span>
                                ))}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── FILTROS ── */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body py-3">
            <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
              <span className="text-muted small fw-semibold" style={{ minWidth: 60 }}>
                <i className="fas fa-layer-group me-1"></i>Etapa:
              </span>
              <button className={`btn btn-sm ${filtroEtapa === 'todas' ? '' : 'btn-outline-secondary'}`}
                style={filtroEtapa === 'todas' ? { backgroundColor: '#00205b', borderColor: '#00205b', color: '#fff' } : undefined}
                onClick={() => setFiltroEtapa('todas')}>
                Todas <span className="badge bg-light text-dark ms-1">{pipelineCompleto.length}</span>
              </button>
              {ETAPAS.map(e => (
                <button key={e.key}
                  className={`btn btn-sm ${filtroEtapa === e.key ? '' : 'btn-outline-secondary'}`}
                  style={filtroEtapa === e.key ? { backgroundColor: '#00205b', borderColor: '#00205b', color: '#fff' } : undefined}
                  onClick={() => setFiltroEtapa(filtroEtapa === e.key ? 'todas' : e.key)}>
                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: e.cor, marginRight: 5 }}></span>
                  {e.label} <span className="badge bg-light text-dark ms-1">{funil.find(f => f.key === e.key)?.count || 0}</span>
                </button>
              ))}
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span className="text-muted small fw-semibold" style={{ minWidth: 60 }}>
                <i className="fas fa-search me-1"></i>Busca:
              </span>
              <input
                value={busca}
                onChange={e => setBusca(e.target.value)}
                placeholder="Empresa, assunto ou operador..."
                className="form-control form-control-sm"
                style={{ maxWidth: 280 }}
              />
              <select value={filtroAlerta} onChange={e => setFiltroAlerta(e.target.value)} className="form-select form-select-sm" style={{ maxWidth: 200 }}>
                <option value="todos">Todos os alertas</option>
                {Object.entries(ALERT_INFO).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
              </select>
              {(filtroEtapa !== 'todas' || filtroAlerta !== 'todos' || busca) && (
                <button className="btn btn-sm btn-outline-danger"
                  onClick={() => { setFiltroEtapa('todas'); setFiltroAlerta('todos'); setBusca(''); }}>
                  <i className="fas fa-times me-1"></i>Limpar
                </button>
              )}
              <span className="badge bg-secondary ms-auto">{filtradas.length} resultado{filtradas.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
        </div>

        {/* ── CARDS DE OPORTUNIDADES ── */}
        {filtradas.length === 0 ? (
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body text-center py-5">
              <i className="fas fa-inbox fa-3x text-muted mb-3 d-block"></i>
              <h6 className="text-muted">Nenhuma oportunidade com os filtros selecionados.</h6>
            </div>
          </div>
        ) : (
          <div className="row g-3 mb-4">
            {filtradas.map(op => {
              const etapa    = ETAPAS.find(e => e.key === normStage(op.estagio_venda)) || ETAPAS[0];
              const isExp    = expandido === op.codigo_reuniao;
              const nota     = op.nota ? parseFloat(op.nota) : null;
              const interesse = interesseNum(op.interesse_nivel);
              const temAlerta = (op.alertas || []).length > 0;
              const chance   = chanceFechamento(op);

              return (
                <div key={op.codigo_reuniao} className="col-md-6 col-xl-4">
                  <div className="card border-0 shadow-sm h-100" style={{ borderLeft: `4px solid ${etapa.cor}`, borderTop: temAlerta ? '2px solid #dc3545' : undefined }}>
                    <div className="card-body p-3">

                      {/* Cabeçalho */}
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                          <strong className="d-block text-truncate" style={{ fontSize: '0.92rem' }}>
                            {op.cliente_empresa || 'Sem empresa'}
                          </strong>
                          <small className="text-muted text-truncate d-block">{op.assunto}</small>
                        </div>
                        <div className="d-flex flex-column align-items-end gap-1 flex-shrink-0 ms-2">
                          <span className="badge" style={{ background: etapa.cor, fontSize: '0.72rem' }}>{etapa.label}</span>
                          {op._origemExpansao && (
                            <span className="badge" style={{ background: '#6610f2', fontSize: '0.68rem' }}>
                              <i className="fas fa-gem me-1"></i>Upsell/Cross-sell
                            </span>
                          )}
                          {op.total_reunioes > 1 && (
                            <span className="badge bg-secondary" style={{ fontSize: '0.68rem' }}>{op.total_reunioes} reuniões</span>
                          )}
                        </div>
                      </div>

                      {/* Barra de interesse */}
                      <div className="mb-2">
                        <div className="d-flex justify-content-between mb-1">
                          <small className="text-muted fw-semibold" style={{ fontSize: '0.7rem' }}>INTERESSE</small>
                          <small className={`badge ${badgeNota(interesse)}`}>{interesse}/10</small>
                        </div>
                        <div className="progress" style={{ height: 5 }}>
                          <div className={`progress-bar ${badgeNota(interesse).replace('badge ', '')}`} style={{ width: `${interesse * 10}%` }}></div>
                        </div>
                      </div>

                      {/* Chance de fechar a venda */}
                      <div className="mb-2" title={chance.fatores.join(' • ')}>
                        <div className="d-flex justify-content-between mb-1">
                          <small className="text-muted fw-semibold" style={{ fontSize: '0.7rem' }}>
                            CHANCE DE FECHAR
                            <i
                              className="fas fa-info-circle ms-1"
                              style={{ cursor: 'pointer', color: '#0d6efd', fontSize: '0.85rem' }}
                              title="Como a chance é calculada?"
                              onClick={e => { e.stopPropagation(); setShowInfoChance(true); }}
                            ></i>
                          </small>
                          <small className="badge" style={{ background: chance.cor }}>
                            {chance.pct}% · {chance.label}
                          </small>
                        </div>
                        <div className="progress" style={{ height: 5 }}>
                          <div className="progress-bar" style={{ width: `${chance.pct}%`, background: chance.cor }}></div>
                        </div>
                      </div>

                      {/* Produtos / serviços do lead */}
                      {(op.produtos || []).length > 0 && (
                        <div className="mb-2">
                          <small className="text-muted fw-semibold d-block mb-1" style={{ fontSize: '0.7rem' }}>
                            PRODUTOS / SERVIÇOS
                          </small>
                          <div className="d-flex flex-wrap gap-1">
                            {op.produtos.map((p, pi) => (
                              <span key={pi} className="badge bg-primary bg-opacity-10 text-primary border border-primary"
                                style={{ fontSize: '0.7rem', fontWeight: 500, whiteSpace: 'normal', textAlign: 'left' }}>
                                {p}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Meta-infos */}
                      <div className="d-flex flex-wrap gap-3 mb-2">
                        {[
                          { lbl: 'OPERADOR', val: op.nome_operador },
                          { lbl: 'DATA',     val: op.data_reuniao || '—' },
                          nota && { lbl: 'NOTA', val: `${nota.toFixed(1)}/10`, badge: badgeNota(nota) },
                          { lbl: 'HÁ',       val: `${op.dias_desde_reuniao}d`, badge: op.dias_desde_reuniao > 10 ? 'bg-danger' : 'bg-secondary' },
                        ].filter(Boolean).map(m => (
                          <div key={m.lbl}>
                            <div className="text-muted" style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.4px' }}>{m.lbl}</div>
                            {m.badge
                              ? <span className={`badge ${m.badge}`} style={{ fontSize: '0.72rem' }}>{m.val}</span>
                              : <div style={{ fontSize: '0.82rem', fontWeight: 600 }}>{m.val}</div>
                            }
                          </div>
                        ))}
                      </div>

                      {/* Alertas */}
                      {temAlerta && (
                        <div className="d-flex flex-wrap gap-1 mb-2">
                          {op.alertas.map(a => (
                            <span key={a} className={`badge bg-${ALERT_INFO[a]?.cls || 'secondary'} bg-opacity-10 border text-dark`} style={{ fontSize: '0.72rem' }}>
                              {ALERT_INFO[a]?.icon} {ALERT_INFO[a]?.label || a}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Box oportunidade */}
                      {op.oportunidade && (
                        <div className="alert alert-success py-2 px-3 mb-2">
                          <strong style={{ fontSize: '0.72rem' }}>OPORTUNIDADE</strong>
                          <p className="mb-0 mt-1" style={{ fontSize: '0.82rem' }}>{op.oportunidade}</p>
                        </div>
                      )}

                      {/* Mover no funil */}
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <small className="text-muted fw-semibold" style={{ fontSize: '0.7rem', flexShrink: 0 }}>
                          <i className="fas fa-layer-group me-1"></i>ETAPA
                        </small>
                        <select
                          className="form-select form-select-sm"
                          style={{
                            fontSize: '0.8rem',
                            borderColor: etapa.cor,
                            color: etapa.cor,
                            fontWeight: 600,
                          }}
                          value={normStage(op.estagio_venda)}
                          disabled={atualizando === op._chave}
                          onChange={e => {
                            const nova = ETAPAS.find(et => et.key === e.target.value);
                            if (nova) atualizarEstagio(op._chave, op.cliente_empresa, nova.label);
                          }}
                        >
                          {ETAPAS.map(et => (
                            <option key={et.key} value={et.key}>{et.label}</option>
                          ))}
                        </select>
                        {atualizando === op._chave && (
                          <span className="spinner-border spinner-border-sm text-muted" style={{ flexShrink: 0 }}></span>
                        )}
                      </div>

                      {/* Estratégia de fechamento com IA */}
                      {!['finalizado', 'perdido'].includes(normStage(op.estagio_venda)) && (
                        <button
                          className="btn btn-sm w-100 mb-2 fw-semibold"
                          onClick={() => pedirEstrategia(op)}
                          style={{
                            background: 'linear-gradient(135deg, #198754 0%, #20c997 100%)',
                            border: 'none', color: '#fff',
                            boxShadow: '0 2px 8px rgba(25,135,84,0.3)',
                          }}
                        >
                          Como fechar essa venda?
                        </button>
                      )}

                      {/* Rodapé do card */}
                      <div className="d-flex justify-content-between align-items-center">
                        <button className="btn btn-link btn-sm p-0 text-primary text-decoration-none"
                          onClick={() => setExpandido(isExp ? null : op.codigo_reuniao)}>
                          <i className={`fas fa-chevron-${isExp ? 'up' : 'down'} me-1`}></i>
                          {isExp ? 'Menos detalhes' : 'Ver detalhes'}
                        </button>
                        {op.total_reunioes > 1 && (
                          <button className="btn btn-link btn-sm p-0 text-muted text-decoration-underline"
                            onClick={() => setModalReunioes(op)}>
                            <i className="fas fa-list me-1"></i>{op.total_reunioes} reuniões
                          </button>
                        )}
                      </div>

                      {isExp && (
                        <div className="mt-3 pt-3 border-top">
                          <div className="mb-2">
                            <small className="text-muted fw-semibold">
                              O QUE PESA NA CHANCE DE FECHAR ({chance.pct}%)
                            </small>
                            <ul className="mb-0 mt-1 ps-3" style={{ fontSize: '0.82rem' }}>
                              {chance.fatores.map((f, fi) => (
                                <li key={fi}>{f}</li>
                              ))}
                            </ul>
                          </div>
                          {op.resumo && (
                            <div className="mb-2">
                              <small className="text-muted fw-semibold">RESUMO MEMOR.IA</small>
                              <p className="mb-0" style={{ fontSize: '0.83rem' }}>{op.resumo}</p>
                            </div>
                          )}
                          {op.pontos_criticos && (
                            <div className="alert alert-danger py-2 px-3 mb-2">
                              <strong style={{ fontSize: '0.72rem' }}>RISCOS</strong>
                              <p className="mb-0 mt-1" style={{ fontSize: '0.82rem' }}>{op.pontos_criticos}</p>
                            </div>
                          )}
                          {op.cliente_dor_principal && (
                            <div className="mb-2">
                              <small className="text-muted fw-semibold">DOR PRINCIPAL</small>
                              <p className="mb-0" style={{ fontSize: '0.83rem' }}>{op.cliente_dor_principal}</p>
                            </div>
                          )}
                          {(op.proximos_passos || []).length > 0 && (
                            <div className="mb-2">
                              <small className="text-muted fw-semibold">PRÓXIMOS PASSOS</small>
                              {op.proximos_passos.map((p, i) => (
                                <p key={i} className="mb-0" style={{ fontSize: '0.83rem' }}>→ {textoProximo(p)}</p>
                              ))}
                            </div>
                          )}
                          {(op.objecoes || []).length > 0 && (
                            <div>
                              <small className="text-muted fw-semibold">OBJEÇÕES</small>
                              {op.objecoes.map((o, i) => (
                                <p key={i} className="mb-0" style={{ fontSize: '0.83rem' }}>• {textoObjecao(o)}</p>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── UPSELL / CROSS-SELL ── */}
        {upsellCrossell.length > 0 && (
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white d-flex justify-content-between align-items-center">
              <div>
                <h6 className="mb-0"><i className="fas fa-gem me-2" style={{ color: '#6610f2' }}></i>Oportunidades de Expansão Upsell / Cross-sell</h6>
                <small className="text-muted">Clientes com potencial de upsell ou cross-sell identificado pela Memor.IA</small>
              </div>
              <span className="badge bg-success">{upsellCrossell.length} cliente{upsellCrossell.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="card-body">
              {/* Filtro por decisão */}
              <div className="d-flex align-items-center gap-2 flex-wrap mb-3">
                <span className="text-muted small fw-semibold">
                  <i className="fas fa-filter me-1"></i>Decisão:
                </span>
                {[
                  { key: 'todas',         label: 'Todas',          icon: 'fa-list' },
                  { key: 'pendente',      label: 'A decidir',      icon: 'fa-hourglass-half' },
                  { key: 'tentar_venda',  label: 'No funil',       icon: 'fa-bullseye' },
                  { key: 'nao_relevante', label: 'Não relevantes', icon: 'fa-ban' },
                ].map(f => {
                  const count = f.key === 'todas'
                    ? upsellCrossell.length
                    : upsellCrossell.filter(o =>
                        (decisoes[(o.cliente_empresa || '').trim()] || 'pendente') === f.key
                      ).length;
                  return (
                    <button
                      key={f.key}
                      className={`btn btn-sm ${filtroExpansao === f.key ? '' : 'btn-outline-secondary'}`}
                      style={filtroExpansao === f.key ? { backgroundColor: '#00205b', borderColor: '#00205b', color: '#fff' } : undefined}
                      onClick={() => setFiltroExpansao(filtroExpansao === f.key && f.key !== 'todas' ? 'todas' : f.key)}
                    >
                      <i className={`fas ${f.icon} me-1`}></i>
                      {f.label} <span className="badge bg-light text-dark ms-1">{count}</span>
                    </button>
                  );
                })}
              </div>

              {/* Funil de expansão — mesmas etapas do funil de vendas */}
              {totalFunilExpansao > 0 && (
                <div className="mb-4 p-3 rounded-3" style={{ background: '#f8f7fd', border: '1px solid #e2d9f3' }}>
                  <h6 className="mb-1" style={{ color: '#6610f2' }}>
                    <i className="fas fa-filter me-2"></i>Funil de Expansão
                  </h6>
                  <small className="text-muted d-block mb-3">
                    Apenas oportunidades de upsell/cross-sell marcadas como "Tentar venda"
                  </small>
                  {funilExpansao.map((etapa, i) => {
                    const escala = Math.max(totalFunilExpansao, 10);
                    const pct = etapa.count > 0
                      ? Math.max((etapa.count / escala) * 100, 5)
                      : 0;
                    const indent = i * 3;
                    return (
                      <div key={etapa.key} className="d-flex align-items-center gap-3 mb-2">
                        <div style={{ width: 110, textAlign: 'right', flexShrink: 0 }}>
                          <span className="fw-semibold" style={{ fontSize: '0.85rem', color: '#6c757d' }}>{etapa.label}</span>
                        </div>
                        <div style={{ flex: 1, position: 'relative', height: 28 }}>
                          <div style={{
                            position: 'absolute', left: `${indent}%`, right: `${indent}%`, top: 0, bottom: 0,
                            background: '#e9ecef', borderRadius: 6,
                          }} />
                          {etapa.count > 0 && (
                            <div style={{
                              position: 'absolute', left: `${indent}%`,
                              width: `${pct * (100 - indent * 2) / 100}%`,
                              top: 0, bottom: 0, background: etapa.cor,
                              borderRadius: '6px 0 0 6px', transition: 'width 0.5s ease',
                              opacity: 0.85,
                            }} />
                          )}
                        </div>
                        <span className="fw-bold" style={{ width: 24, textAlign: 'right', fontSize: '0.9rem', color: '#343a40' }}>{etapa.count}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="row g-3">
                {[...upsellCrossell].filter(o => {
                  if (filtroExpansao === 'todas') return true;
                  return (decisoes[(o.cliente_empresa || '').trim()] || 'pendente') === filtroExpansao;
                }).sort((a, b) => {
                  const ordem = { pendente: 0, tentar_venda: 1, nao_relevante: 2 };
                  const dA = decisoes[(a.cliente_empresa || '').trim()] || 'pendente';
                  const dB = decisoes[(b.cliente_empresa || '').trim()] || 'pendente';
                  return ordem[dA] - ordem[dB];
                }).map(op => {
                  const tipo = op._tipoExpansao;
                  const empresaChave = (op.cliente_empresa || '').trim();
                  const decisao = decisoes[empresaChave] || 'pendente';
                  const badge =
                    tipo === 'ambos'     ? { label: 'Upsell + Cross-sell', cls: 'bg-purple',  style: { background: '#6610f2' } } :
                    tipo === 'upsell'    ? { label: 'Upsell',              cls: 'bg-primary',  style: {} } :
                    tipo === 'crosssell' ? { label: 'Cross-sell',          cls: '', style: { background: '#00205b', color: '#fff' } } :
                                          { label: 'Oportunidade',         cls: 'bg-success',  style: {} };
                  const etapa = ETAPAS.find(e => e.key === normStage(op.estagio_venda)) || ETAPAS[0];
                  return (
                    <div key={op._chave} className="col-md-6 col-xl-4">
                      <div className="card border h-100" style={{ borderLeft: `3px solid ${etapa.cor}`, opacity: decisao === 'nao_relevante' ? 0.55 : 1 }}>
                        <div className="card-body p-3">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                              <strong className="d-block text-truncate" style={{ fontSize: '0.92rem' }}>{op.cliente_empresa || 'Sem empresa'}</strong>
                              <small className="text-muted">{op.nome_operador} • {op.data_reuniao || '—'}</small>
                            </div>
                            <div className="d-flex flex-column gap-1 align-items-end ms-2 flex-shrink-0">
                              <span className={`badge ${badge.cls}`} style={{ fontSize: '0.7rem', ...badge.style }}>{badge.label}</span>
                              <span className="badge" style={{ background: etapa.cor, fontSize: '0.68rem' }}>{etapa.label}</span>
                            </div>
                          </div>
                          <div className="mb-2">
                            <div className="d-flex justify-content-between mb-1">
                              <small className="text-muted fw-semibold" style={{ fontSize: '0.7rem' }}>INTERESSE</small>
                              <small className={`badge ${badgeNota(interesseNum(op.interesse_nivel))}`}>{op.interesse_nivel || 0}/10</small>
                            </div>
                            <div className="progress" style={{ height: 4 }}>
                              <div className={`progress-bar ${badgeNota(interesseNum(op.interesse_nivel)).replace('badge ', '')}`}
                                style={{ width: `${interesseNum(op.interesse_nivel) * 10}%` }}></div>
                            </div>
                          </div>
                          <div className="alert alert-success py-2 px-3 mb-2">
                            <strong style={{ fontSize: '0.72rem' }}>OPORTUNIDADE IDENTIFICADA</strong>
                            <p className="mb-0 mt-1" style={{ fontSize: '0.82rem', lineHeight: 1.5 }}>{op.oportunidade}</p>
                          </div>

                          {/* Decisão: tentar venda (vira Lead no funil) ou não relevante */}
                          <div className="d-flex gap-2 mb-2">
                            <button
                              className={`btn btn-sm flex-grow-1 ${decisao === 'tentar_venda' ? 'btn-success' : 'btn-outline-success'}`}
                              disabled={decidindo === empresaChave}
                              onClick={() => decidirExpansao(op.cliente_empresa, 'tentar_venda')}
                            >
                              {decisao === 'tentar_venda'
                                ? <><i className="fas fa-check me-1"></i>No funil como Lead</>
                                : <><i className="fas fa-bullseye me-1"></i>Tentar venda</>}
                            </button>
                            <button
                              className={`btn btn-sm flex-grow-1 ${decisao === 'nao_relevante' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                              disabled={decidindo === empresaChave}
                              onClick={() => decidirExpansao(op.cliente_empresa, 'nao_relevante')}
                            >
                              {decisao === 'nao_relevante'
                                ? <><i className="fas fa-check me-1"></i>Não relevante</>
                                : <><i className="fas fa-ban me-1"></i>Não é relevante</>}
                            </button>
                          </div>

                          {/* Etapa do funil (só quando está tentando a venda) */}
                          {decisao === 'tentar_venda' && (() => {
                            const entrada = pipelineCompleto.find(p =>
                              (p.cliente_empresa || '').trim().toLowerCase() === empresaChave.toLowerCase()
                            );
                            const estagioAtual = normStage(
                              estagioOverrides[empresaChave] || entrada?.estagio_venda || op.estagio_venda || 'Lead'
                            );
                            const etapaAtual = ETAPAS.find(e => e.key === estagioAtual) || ETAPAS[0];
                            return (
                              <div className="d-flex align-items-center gap-2 mb-2">
                                <small className="text-muted fw-semibold" style={{ fontSize: '0.7rem', flexShrink: 0 }}>
                                  <i className="fas fa-layer-group me-1"></i>ETAPA
                                </small>
                                <select
                                  className="form-select form-select-sm"
                                  style={{
                                    fontSize: '0.8rem',
                                    borderColor: etapaAtual.cor,
                                    color: etapaAtual.cor,
                                    fontWeight: 600,
                                  }}
                                  value={etapaAtual.key}
                                  disabled={atualizando === empresaChave}
                                  onChange={e => {
                                    const nova = ETAPAS.find(et => et.key === e.target.value);
                                    if (nova) atualizarEstagio(empresaChave, op.cliente_empresa, nova.label);
                                  }}
                                >
                                  {ETAPAS.map(et => (
                                    <option key={et.key} value={et.key}>{et.label}</option>
                                  ))}
                                </select>
                                {atualizando === empresaChave && (
                                  <span className="spinner-border spinner-border-sm text-muted" style={{ flexShrink: 0 }}></span>
                                )}
                              </div>
                            );
                          })()}

                          <button className="btn btn-sm btn-outline-secondary w-100"
                            onClick={() => setModalReunioes(op)}>
                            <i className="fas fa-list me-1"></i>Ver {op.total_reunioes} reunião{op.total_reunioes !== 1 ? 'ões' : ''}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── OBJEÇÕES + PRÓXIMAS AÇÕES ── */}
        <div className="row g-4 mb-4">

          <div className="col-md-8">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white" style={{ borderBottom: '2px solid #dc3545' }}>
                <h6 className="mb-0"><i className="fas fa-hand-paper me-2 text-danger"></i>Principais Objeções do Pipeline</h6>
              </div>
              <div className="card-body">
                {objecoesFreq.length === 0 ? (
                  <p className="text-muted text-center py-3">Nenhuma objeção registrada ainda.</p>
                ) : (
                  <>
                    {/* Gráfico por categoria — legível e comparável */}
                    <small className="text-muted d-block mb-2">
                      <i className="fas fa-tags me-1"></i>Objeções agrupadas por categoria
                    </small>
                    <ResponsiveContainer width="100%" height={Math.max(120, objecoesPorCategoria.length * 38)}>
                      <BarChart data={objecoesPorCategoria} layout="vertical" margin={{ left: 8, right: 30, top: 4, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                        <YAxis type="category" dataKey="label" width={140} tick={{ fontSize: 12 }} />
                        <Tooltip content={<TooltipObjecoes />} cursor={{ fill: 'rgba(13,110,253,0.06)' }} />
                        <Bar dataKey="count" radius={[0, 5, 5, 0]} label={{ position: 'right', fontSize: 11 }}>
                          {objecoesPorCategoria.map((c, i) => (
                            <Cell key={i} fill={c.cor} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>

                    {/* Tabela com o texto completo de cada objeção */}
                    <div className="table-responsive mt-3">
                      <table className="table table-sm table-hover mb-0">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: 140 }}>Categoria</th>
                            <th>Objeção</th>
                            <th className="text-end" style={{ width: 60 }}>Freq.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {objecoesFreq.map((o, i) => {
                            const cat = categorizarObjecao(o.texto);
                            return (
                              <tr key={i}>
                                <td>
                                  <span className="badge" style={{ background: cat.cor, fontSize: '0.72rem' }}>{cat.label}</span>
                                </td>
                                <td style={{ fontSize: '0.88rem' }}>{o.texto}</td>
                                <td className="text-end">
                                  <span className="badge bg-secondary">{o.count}×</span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white" style={{ borderBottom: '2px solid #0d6efd' }}>
                <h6 className="mb-0"><i className="fas fa-tasks me-2 text-primary"></i>Próximas Ações</h6>
              </div>
              <div className="card-body p-0">
                {proximasAcoes.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    <i className="fas fa-clipboard-check fa-2x mb-2 d-block"></i>
                    <small>Nenhuma ação registrada.</small>
                  </div>
                ) : proximasAcoes.map((acao, i) => (
                  <div key={i} className="d-flex gap-2 p-3" style={{ borderBottom: i < proximasAcoes.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                    <div className="d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{ width: 30, height: 30, background: '#0d6efd1a', borderRadius: 8, color: '#0d6efd', fontWeight: 800 }}>
                      →
                    </div>
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <p className="mb-0 fw-semibold" style={{ fontSize: '0.87rem', lineHeight: 1.4 }}>{acao.txt}</p>
                      <small className="text-muted">{acao.empresa || '—'} · {acao.operador}</small>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>

      </div>
      <style>{`.modal.show { display: block; }`}</style>
    </div>
  );
}
